import React, { useState, useEffect, useCallback } from 'react';
import { HiXMark, HiDevicePhoneMobile, HiClock, HiClipboardDocument, HiArrowTopRightOnSquare, HiMoon, HiExclamationTriangle } from 'react-icons/hi2';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase/config';
import { TerraDevice } from '../types/user';

interface WearableDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string;
    name: string;
    surname: string;
    email: string;
  };
  onDeviceDeauthed?: () => void;
}

const WearableDetailsDialog: React.FC<WearableDetailsDialogProps> = ({
  isOpen,
  onClose,
  user,
  onDeviceDeauthed,
}) => {
  const [devices, setDevices] = useState<TerraDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [copiedDeviceId, setCopiedDeviceId] = useState<string | null>(null);
  const [deauthConfirm, setDeauthConfirm] = useState<{ deviceId: string; resource: string } | null>(null);
  const [deauthLoading, setDeauthLoading] = useState(false);
  const [deauthResult, setDeauthResult] = useState<{ success: boolean; message: string } | null>(null);

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const privateRef = doc(db, 'users', user.id, 'private', 'data');
      const privateSnap = await getDoc(privateRef);
      if (privateSnap.exists()) {
        const data = privateSnap.data();
        setDevices((data?.deviceIDs || []) as TerraDevice[]);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Failed to fetch user devices:', error);
    } finally {
      setDevicesLoading(false);
    }
  }, [user.id]);

  // Fetch fresh devices when the dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchDevices();
    }
  }, [isOpen, fetchDevices]);

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getResourceIcon = (resource: string) => {
    const resourceLower = resource.toLowerCase();
    
    if (resourceLower.includes('fitbit')) {
      return '🟣'; // Purple circle for Fitbit
    } else if (resourceLower.includes('garmin')) {
      return '🔵'; // Blue circle for Garmin
    } else if (resourceLower.includes('apple')) {
      return '⚪'; // White circle for Apple Watch
    } else if (resourceLower.includes('oura')) {
      return '⚫'; // Black circle for Oura Ring
    } else if (resourceLower.includes('whoop')) {
      return '🟡'; // Yellow circle for WHOOP
    } else if (resourceLower.includes('polar')) {
      return '🔴'; // Red circle for Polar
    } else if (resourceLower.includes('suunto')) {
      return '🟠'; // Orange circle for Suunto
    } else if (resourceLower.includes('samsung')) {
      return '🟢'; // Green circle for Samsung
    }
    
    return '🔘'; // Default gray circle
  };

  const getResourceDisplayName = (resource: string) => {
    // Convert resource names to more user-friendly format
    const resourceMap: { [key: string]: string } = {
      'FITBIT': 'Fitbit',
      'GARMIN': 'Garmin',
      'APPLE_HEALTH': 'Apple Health',
      'APPLE_WATCH': 'Apple Watch',
      'OURA': 'Oura Ring',
      'WHOOP': 'WHOOP',
      'POLAR': 'Polar',
      'SUUNTO': 'Suunto',
      'SAMSUNG': 'Samsung Health',
      'STRAVA': 'Strava',
      'GOOGLE_FIT': 'Google Fit'
    };
    
    return resourceMap[resource.toUpperCase()] || resource;
  };

  const activeDevices = devices.filter(device => !device.expired);
  const expiredDevices = devices.filter(device => device.expired);

  const handleDeviceClick = async (deviceId: string) => {
    try {
      // Copy device ID to clipboard
      await navigator.clipboard.writeText(deviceId);
      setCopiedDeviceId(deviceId);
      
      // Clear the copied state after 2 seconds
      setTimeout(() => {
        setCopiedDeviceId(null);
      }, 2000);
      
      // Open Terra dashboard in new tab
      window.open('https://dashboard.tryterra.co/dashboard/users', '_blank');
    } catch (error) {
      console.error('Failed to copy device ID to clipboard:', error);
      // Fallback: still open the dashboard even if clipboard fails
      window.open('https://dashboard.tryterra.co/dashboard/users', '_blank');
    }
  };

  const handleSeeSleepData = (deviceId: string) => {
    // Generate the Google Cloud Storage URL for sleep data filtered by device ID
    // The pageState parameter contains URL-encoded filter information
    const encodedDeviceId = encodeURIComponent(`"${deviceId}"`);
    const pageStateFilter = `%22StorageObjectListTable%22:%22f%22:%22%255B%257B_22k_22_3A_22_22_2C_22t_22_3A10_2C_22v_22_3A_22_5C_22${encodedDeviceId}_5C_22_22%257D%255D%22`;
    
    const sleepDataUrl = `https://console.cloud.google.com/storage/browser/terra-production/2022-03-16/sleep;tab=objects?pageState=(${pageStateFilter})&project=peak-x-937b6&prefix=${deviceId}&forceOnObjectsSortingFiltering=false`;
    
    window.open(sleepDataUrl, '_blank');
  };

  const handleDeauth = async () => {
    if (!deauthConfirm) return;

    setDeauthLoading(true);
    setDeauthResult(null);

    try {
      const adminDeauthDevice = httpsCallable(functions, 'adminDeauthDevice');
      const result = await adminDeauthDevice({
        targetUserId: user.id,
        deviceId: deauthConfirm.deviceId,
      });

      const data = result.data as { success: boolean; message: string };

      setDeauthResult({ success: true, message: data.message });

      // Re-fetch the user's devices to get the updated list
      await fetchDevices();
      onDeviceDeauthed?.();

      // Close confirmation after a short delay
      setTimeout(() => {
        setDeauthConfirm(null);
        setDeauthResult(null);
      }, 2000);
    } catch (error: any) {
      console.error('Error deauthing device:', error);
      setDeauthResult({
        success: false,
        message: error.message || 'Failed to deauthenticate device',
      });
    } finally {
      setDeauthLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Dialog */}
        <div className="inline-block align-bottom bg-base-100 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-base-100 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <HiDevicePhoneMobile className="w-6 h-6 text-primary" />
                <h3 className="text-lg font-medium text-base-content">
                  Wearable Devices
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-base-content/50 hover:text-base-content/80 transition-colors"
              >
                <HiXMark className="w-5 h-5" />
              </button>
            </div>

            {/* User info */}
            <div className="mb-6 p-3 bg-base-100 rounded-lg">
              <p className="text-sm text-base-content/80">User</p>
              <p className="font-medium text-base-content">
                {user.name} {user.surname}
              </p>
              <p className="text-sm text-base-content/60">{user.email}</p>
            </div>

            {/* Devices list */}
            <div className="space-y-4">
              {/* Loading state */}
              {devicesLoading && devices.length === 0 && (
                <div className="text-center py-8">
                  <span className="loading loading-spinner loading-md text-primary"></span>
                  <p className="text-sm text-base-content/60 mt-2">Loading devices...</p>
                </div>
              )}

              {/* Active devices */}
              {activeDevices.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-base-content mb-3 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    Active Devices ({activeDevices.length})
                  </h4>
                  <div className="space-y-2">
                    {activeDevices.map((device, index) => (
                      <div 
                        key={`${device.id}-${index}`}
                        className="border border-green-200 bg-green-500/10 rounded-lg relative"
                      >
                        <div className="absolute top-2 right-2 flex items-center space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSeeSleepData(device.id);
                            }}
                            className="btn btn-xs btn-circle btn-ghost text-green-700 hover:bg-green-200"
                            title="View sleep data in Google Cloud Storage"
                          >
                            <HiMoon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeauthResult(null);
                              setDeauthConfirm({ deviceId: device.id, resource: device.resource });
                            }}
                            className="btn btn-xs btn-circle btn-ghost text-red-500 hover:bg-red-500/15"
                            title="Disconnect this device"
                          >
                            <HiXMark className="w-4 h-4" />
                          </button>
                        </div>
                        <div 
                          onClick={() => handleDeviceClick(device.id)}
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-green-500/15 transition-all duration-200 group"
                          title="Click to copy device ID and open Terra dashboard"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-lg">{getResourceIcon(device.resource)}</span>
                            <div>
                              <p className="font-medium text-base-content group-hover:text-green-700 dark:text-green-400">
                                {getResourceDisplayName(device.resource)}
                              </p>
                              <div className="flex items-center space-x-2">
                                <p className="text-xs text-base-content/60 font-mono">
                                  ID: {device.id}
                                </p>
                                {copiedDeviceId === device.id ? (
                                  <span className="text-xs text-green-600 flex items-center">
                                    <HiClipboardDocument className="w-3 h-3 mr-1" />
                                    Copied!
                                  </span>
                                ) : (
                                  <HiArrowTopRightOnSquare className="w-3 h-3 text-base-content/50 group-hover:text-green-600" />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center text-xs text-base-content/60">
                              <HiClock className="w-3 h-3 mr-1" />
                              Connected
                            </div>
                            <p className="text-xs text-base-content/60">
                              {formatDate(device.connectedAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expired devices */}
              {expiredDevices.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-base-content mb-3 flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    Expired Devices ({expiredDevices.length})
                  </h4>
                  <div className="space-y-2">
                    {expiredDevices.map((device, index) => (
                      <div 
                        key={`${device.id}-${index}`}
                        className="border border-red-200 bg-red-500/10 rounded-lg opacity-75 relative"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSeeSleepData(device.id);
                          }}
                          className="absolute top-2 right-2 btn btn-xs btn-circle btn-ghost text-red-700 hover:bg-red-200"
                          title="View sleep data in Google Cloud Storage"
                        >
                          <HiMoon className="w-4 h-4" />
                        </button>
                        <div 
                          onClick={() => handleDeviceClick(device.id)}
                          className="flex items-center justify-between p-3 cursor-pointer hover:opacity-90 hover:bg-red-500/15 transition-all duration-200 group"
                          title="Click to copy device ID and open Terra dashboard"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-lg grayscale">{getResourceIcon(device.resource)}</span>
                            <div>
                              <p className="font-medium text-base-content/80 group-hover:text-red-700 dark:text-red-400">
                                {getResourceDisplayName(device.resource)}
                              </p>
                              <div className="flex items-center space-x-2">
                                <p className="text-xs text-base-content/50 font-mono">
                                  ID: {device.id}
                                </p>
                                {copiedDeviceId === device.id ? (
                                  <span className="text-xs text-red-600 flex items-center">
                                    <HiClipboardDocument className="w-3 h-3 mr-1" />
                                    Copied!
                                  </span>
                                ) : (
                                  <HiArrowTopRightOnSquare className="w-3 h-3 text-base-content/50 group-hover:text-red-600" />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center text-xs text-red-500">
                              <HiClock className="w-3 h-3 mr-1" />
                              Expired
                            </div>
                            <p className="text-xs text-base-content/50">
                              {formatDate(device.connectedAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No devices */}
              {!devicesLoading && devices.length === 0 && (
                <div className="text-center py-8">
                  <HiDevicePhoneMobile className="w-12 h-12 text-base-content/50 mx-auto mb-4" />
                  <p className="text-base-content/60">No wearable devices found</p>
                  <p className="text-sm text-base-content/50">This user hasn't connected any devices yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-base-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-focus focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Deauth Confirmation Dialog */}
      {deauthConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => { if (!deauthLoading) { setDeauthConfirm(null); setDeauthResult(null); } }} />
          <div className="relative bg-base-100 rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                <HiExclamationTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-base-content">Disconnect Device</h3>
            </div>

            <p className="text-sm text-base-content/80 mb-2">
              Are you sure you want to disconnect <span className="font-medium">{getResourceDisplayName(deauthConfirm.resource)}</span> for{' '}
              <span className="font-medium">{user.name} {user.surname}</span>?
            </p>
            <p className="text-xs text-base-content/50 mb-1 font-mono">Device ID: {deauthConfirm.deviceId}</p>
            <p className="text-xs text-red-500 mb-5">
              This will deauthenticate the device via Terra and mark it as expired. The user will need to reconnect.
            </p>

            {deauthResult && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${deauthResult.success ? 'bg-green-500/10 text-green-700' : 'bg-red-500/10 text-red-700'}`}>
                {deauthResult.message}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setDeauthConfirm(null); setDeauthResult(null); }}
                disabled={deauthLoading}
                className="px-4 py-2 text-sm font-medium text-base-content/80 bg-base-200 rounded-md hover:bg-base-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeauth}
                disabled={deauthLoading || deauthResult?.success === true}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deauthLoading ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    <span>Disconnecting...</span>
                  </>
                ) : (
                  <span>Disconnect</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WearableDetailsDialog;
